import type { GiftCardsByFiltersQueryVariables } from "@/__generated__/graphql";
import { GiftCardRoleEnum, GiftCardsByFiltersDocument } from "@/__generated__/graphql";
import { mapToGiftCardHistoryItem } from "@/containers/profile/account-gift-card/account-gift-card-history-component/lib";
import type { GiftCardHistoryTab } from "@/containers/profile/account-gift-card/account-gift-card-history-component/types";
import { useAuthState } from "@/store/auth";
import { useQuery } from "@apollo/client";

/**
 * Fetches gift cards for the current client via giftCardsByFilters.
 *
 * When a tab is provided, the backend `role` filter is used:
 *   - "sent"     → role: SENDER   (gift cards where client is the sender)
 *   - "received" → role: RECIPIENT (gift cards where client is the recipient)
 * When no tab is provided, all gift cards for the client are returned.
 *
 * @param tab - "sent" | "received" — mapped to backend GiftCardRoleEnum filter
 * @param limit - page size sent to the backend (default 50)
 */
export const useGiftCardsByFiltersQuery = ({
  tab,
  limit,
}: {
  tab?: GiftCardHistoryTab;
  limit?: number;
} = {}) => {
  const clientId = useAuthState((state) => state.data.userInfo.id);

  const role =
    tab === "sent"
      ? GiftCardRoleEnum.Sender
      : tab === "received"
        ? GiftCardRoleEnum.Recipient
        : undefined;

  const filters: GiftCardsByFiltersQueryVariables["filters"] = {
    clientId,
    limit: limit ?? 50,
    offset: 0,
    ...(role ? { role } : {}),
  };

  const result = useQuery(GiftCardsByFiltersDocument, {
    skip: !clientId,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
    variables: { filters },
  });

  const rawGiftCards = result.data?.giftCardsByFilters?.giftCards ?? [];
  const total = result.data?.giftCardsByFilters?.count ?? 0;
  const allItems = rawGiftCards.map((gc) => mapToGiftCardHistoryItem(gc, clientId));
  const list = allItems;
  // Note: no client-side filtering needed — the backend `role` filter handles sent/received separation
  const canLoadMore = allItems.length < total;
  const loading = result.loading && !result.data;

  const loadMore = () => {
    if (!canLoadMore || loading || !clientId) return;
    result.fetchMore({
      variables: {
        filters: {
          ...result.variables?.filters,
          offset: allItems.length,
        },
      },
    });
  };

  return {
    ...result,
    list,
    total,
    canLoadMore,
    loading,
    loadMore,
  };
};
